import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import { useAuth } from "../../context/AuthProvider";
import API_BASE_URL from "../../config/apiConfig";
import Swal from "sweetalert2";
import back from "../../assets/images/icon-back.svg";
import hide from "../../assets/images/hide.svg";
import show from "../../assets/images/show.svg";

const EditPatient: React.FC = () => {
  const { token } = useAuth();
  const { patient_id } = useParams<{ patient_id: string }>();
  const navigate = useNavigate();

  const [profileStatus, setProfileStatus] = useState<"lead" | "full">("full");
  const [expandToFull, setExpandToFull] = useState(false);

  const [showPasswordField, setShowPasswordField] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    cnp: "",
    type: "",
    das: "",
    age_range: "",
    category: "",
    parentFirstName: "",
    parentLastName: "",
    parentEmail: "",
    parentPhone: "",
    country: "",
    zone: "",
    city: "",
    parentPassword: "",
  });

  const [patientTypes, setPatientTypes] = useState<{ key: string; value: string }[]>([]);
  const [ageRanges, setAgeRanges] = useState<{ key: string; value: string }[]>([]);
  const [categories, setCategories] = useState<{ key: string; value: string }[]>([]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const fetchData = async (endpoint: string) => {
          const response = await fetch(`${API_BASE_URL}/types/${endpoint}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) throw new Error("Failed to fetch dropdown data");
          const data = await response.json();
          return data.data;
        };

        const [types, ranges, cats] = await Promise.all([
          fetchData("patient-types"),
          fetchData("age-ranges"),
          fetchData("categories"),
        ]);

        setPatientTypes(types);
        setAgeRanges(ranges);
        setCategories(cats);
      } catch {
        Swal.fire({
          icon: "error",
          title: "Eroare",
          text: "Eroare la încărcarea listelor dropdown.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    };

    if (token) fetchDropdowns();
  }, [token]);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/patient/${patient_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Pacientul nu a fost găsit.");

        const data = await response.json();

        const ps = (data.profile_status || "full") as "lead" | "full";
        setProfileStatus(ps);

        setFormData({
          firstname: data.firstname || "",
          lastname: data.lastname || "",
          cnp: data.cnp || "",
          type: data.type || "",
          das: data.das || "",
          age_range: data.age_range || "",
          category: data.category || "",
          parentFirstName: data.parent_firstname || "",
          parentLastName: data.parent_lastname || "",
          parentEmail: data.parent_email || "",
          parentPhone: data.parent_phone || "",
          country: data.country || "",
          zone: data.zone || "",
          city: data.city || "",
          parentPassword: "",
        });
      } catch (err: any) {
        Swal.fire({
          icon: "error",
          title: "Eroare",
          text: err.message || "A apărut o eroare necunoscută.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    };

    if (patient_id && token) fetchPatient();
  }, [patient_id, token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let url = `${API_BASE_URL}/patient/${patient_id}`;
      let method: "PUT" | "POST" = "PUT";

      // lead => dacă a ales convert, facem POST convert
      if (profileStatus === "lead" && expandToFull) {
        const cnpRegex = /^[0-9]{13}$/;
        if (!cnpRegex.test(formData.cnp)) {
          Swal.fire({
            icon: "error",
            title: "CNP invalid",
            text: "CNP-ul trebuie să conțină exact 13 cifre.",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2500,
          });
          return;
        }
        url = `${API_BASE_URL}/patient/${patient_id}/convert`;
        method = "POST";
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || "A apărut o eroare.");
      }

      Swal.fire({
        icon: "success",
        title:
          profileStatus === "lead" && expandToFull
            ? "Pacient convertit în pacient normal!"
            : "Pacient actualizat cu succes!",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });

      setTimeout(() => navigate("/webmaster/patients"), 1500);
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Eroare",
        text: err.message || "A apărut o eroare.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const showFullForm = profileStatus === "full" || expandToFull;

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />

      <div className="page-content">
        <h1 className="page-title">
          Editează Pacient {profileStatus === "lead" ? "(consult)" : ""}
        </h1>

        <div className="table-top">
          <div className="page-return">
            <button className="button-return" onClick={() => navigate(-1)}>
              <img src={back} alt="Înapoi" className="back-icon" /> Înapoi
            </button>
          </div>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <label>Nume:</label>
              <input
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                placeholder="Numele"
                required
              />
            </div>

            <div className="form-row">
              <label>Prenume:</label>
              <input
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                placeholder="Prenumele"
                required
              />
            </div>

            {/* LEAD: telefonul e important */}
            {profileStatus === "lead" && (
              <div className="form-row">
                <label>Telefon:</label>
                <input
                  type="text"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleChange}
                  placeholder="Telefon"
                  required
                />
              </div>
            )}

            {profileStatus === "lead" && !expandToFull && (
              <div className="form-row">
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => setExpandToFull(true)}
                >
                  Completează și transformă în pacient normal
                </button>
              </div>
            )}

            {showFullForm && (
              <>
                <div className="form-row">
                  <label>CNP:</label>
                  <input
                    type="text"
                    name="cnp"
                    value={formData.cnp}
                    onChange={handleChange}
                    placeholder="CNP"
                    maxLength={13}
                    required
                  />
                </div>

                <div className="form-row">
                  <label>Tip:</label>
                  <select name="type" value={formData.type} onChange={handleChange} required>
                    <option value="">Selectează tipul</option>
                    {patientTypes.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.value}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.type === "intern" && (
                  <div className="form-row">
                    <label>DAS:</label>
                    <input
                      type="text"
                      name="das"
                      value={formData.das}
                      onChange={handleChange}
                      placeholder="DAS"
                    />
                  </div>
                )}

                <div className="form-row flex-row">
                  <div className="flex-input">
                    <label>Vârstă:</label>
                    <select name="age_range" value={formData.age_range} onChange={handleChange} required>
                      <option value="">Selectează vârsta</option>
                      {ageRanges.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.value}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-input">
                    <label>Categorie:</label>
                    <select name="category" value={formData.category} onChange={handleChange} required>
                      <option value="">Selectează categoria</option>
                      {categories.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <h3 className="form-subtitle">Reprezentant Legal</h3>

                <div className="form-row flex-row">
                  <div className="flex-input">
                    <label>Prenume:</label>
                    <input
                      type="text"
                      name="parentFirstName"
                      value={formData.parentFirstName}
                      onChange={handleChange}
                      placeholder="Prenume reprezentant legal"
                      required
                    />
                  </div>

                  <div className="flex-input">
                    <label>Nume:</label>
                    <input
                      type="text"
                      name="parentLastName"
                      value={formData.parentLastName}
                      onChange={handleChange}
                      placeholder="Nume reprezentant legal"
                      required
                    />
                  </div>
                </div>

                <div className="form-row flex-row">
                  <div className="flex-input">
                    <label>Email:</label>
                    <input
                      type="email"
                      name="parentEmail"
                      value={formData.parentEmail}
                      onChange={handleChange}
                      placeholder="Email reprezentant legal"
                      required
                    />
                  </div>

                  <div className="flex-input">
                    <label>Telefon:</label>
                    <input
                      type="text"
                      name="parentPhone"
                      value={formData.parentPhone}
                      onChange={handleChange}
                      placeholder="Telefon reprezentant legal"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <label>Țară:</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Țara"
                    required
                  />
                </div>

                <div className="form-row flex-row">
                  <div className="flex-input">
                    <label>Județ/Regiune:</label>
                    <input
                      type="text"
                      name="zone"
                      value={formData.zone}
                      onChange={handleChange}
                      placeholder="Județ/Regiune"
                      required
                    />
                  </div>
                  <div className="flex-input">
                    <label>Localitate:</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Localitate"
                      required
                    />
                  </div>
                </div>

                <div className="form-row edit-password">
                  <button
                    type="button"
                    onClick={() => setShowPasswordField(!showPasswordField)}
                  >
                    {showPasswordField ? "Ascunde câmpul de parolă" : "Modifică parola"}
                  </button>
                </div>

                {showPasswordField && (
                  <div className="form-row password-field">
                    <label>Parolă:</label>
                    <div>
                      <input
                        type={isPasswordVisible ? "text" : "password"}
                        name="parentPassword"
                        value={formData.parentPassword}
                        onChange={handleChange}
                        placeholder="Parola"
                      />
                      <button
                        type="button"
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      >
                        {isPasswordVisible ? (
                          <img className="hide-password" src={hide} alt="Ascunde" />
                        ) : (
                          <img className="show-password" src={show} alt="Arată" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="form-row form-actions">
              <button className="cancel-btn" onClick={() => navigate(-1)} type="button">
                Anulează
              </button>
              <button className="primary-btn" type="submit">
                {profileStatus === "lead" && expandToFull ? "Transformă în pacient normal" : "Salvează"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPatient;